import { cn } from '@/lib/utils'

interface SheetSectionCardProps {
  /** Omit when the body renders its own title row (e.g. title + status). */
  title?: React.ReactNode
  body: React.ReactNode
  className?: string
}

/** Titled section card shared across details sheets (Calls, Patients, …). */
export function SheetSectionCard({
  title,
  body,
  className,
}: SheetSectionCardProps) {
  return (
    <div
      className={cn(
        'bg-app border border-neutral-ui-border shadow-[var(--shadow-md)] rounded-lg p-3',
        className
      )}>
      {title != null && (
        <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide">
          {title}
        </p>
      )}
      {body}
    </div>
  )
}

/** Label/value row; null, undefined, or blank values render as "-". */
export function MetaRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim().length === 0)
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="min-w-0 text-right break-words">
        {empty ? '-' : value}
      </span>
    </div>
  )
}

/** Titled card of `MetaRow`s. */
export function MetaCard({
  title,
  rows,
}: {
  title: string
  rows: { label: string; value: React.ReactNode }[]
}) {
  return (
    <SheetSectionCard
      title={title}
      body={
        <div className="divide-y rounded-md border px-3">
          {rows.map((r) => (
            <MetaRow key={r.label} label={r.label} value={r.value} />
          ))}
        </div>
      }
    />
  )
}
